import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Phone, Clock, ArrowRight, CheckCircle2, Sparkles, Star,
  Brain, Rocket, Users, Award, Target, Briefcase, Monitor, Cpu, Building2,
  GraduationCap, ChevronRight, Send, Shield, Zap, Layers, TrendingUp, Bot
} from 'lucide-react';
import PageMeta from '../components/PageMeta';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ------------------------- Premium AI image library ------------------------ */
const IMG = {
  robotOffice: 'https://customer-assets-agu9un31.emergentagent.net/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/7972pqhd_ai-robot-working-in-the-office-2026-01-07-00-29-03-utc.webp',
  touchTech: 'https://customer-assets-agu9un31.emergentagent.net/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/qmwa0e8d_ai-touch-technology-concept-glowing-interface-conn-2026-01-11-08-36-44-utc.webp',
  vrCubes: 'https://customer-assets-agu9un31.emergentagent.net/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/3qs4a6h1_adult-wearing-vr-headset-with-floating-cubes-2026-03-26-01-04-38-utc.webp',
  manPhone: 'https://customer-assets-agu9un31.emergentagent.net/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/605z465e_artificial-intelligence-concept-with-man-and-phone-2026-01-07-06-32-50-utc.webp',
};

const CENTRE = {
  address: '404, Tower A – Ithum Building, Sector 62, Noida',
  phone: '9891990666',
  hours: 'Mon – Sat · 10:00 AM to 7:00 PM',
  mapEmbed: 'https://www.google.com/maps?q=Ithum+Tower+A+Sector+62+Noida&output=embed',
};

/* ================================ SECTION 1 ============================== */
const Hero = () => (
  <section className="relative overflow-hidden bg-slate-950 text-white">
    <div className="absolute inset-0 opacity-40">
      <img src={IMG.touchTech} alt="AI interface" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/80 to-slate-950" />
    </div>
    <div className="relative container mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24 lg:py-28">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">
          <MapPin className="w-3.5 h-3.5" /> Now Open · Sector 62, Noida
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
          India's First <span className="text-orange-400">AI Experience Centre</span>
          <br className="hidden md:block" /> for the AI-First Workforce
        </h1>
        <p className="mt-5 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
          Step inside a hands-on innovation lab where AI stops being buzzword and becomes career-changing skill. Live studios, immersive labs, industry mentors — right in the heart of Noida.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="#book-visit"
            data-testid="noida-hero-book-visit"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-3.5 text-base shadow-2xl shadow-orange-500/30 transition-all"
          >
            Book Your Free Visit <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href={`tel:${CENTRE.phone}`}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-full px-6 py-3.5 text-base backdrop-blur-md"
          >
            <Phone className="w-4 h-4" /> Call {CENTRE.phone}
          </a>
        </div>
        <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-300">
          <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Industry-Grade Studios</span>
          <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Live Mentor Access</span>
          <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Placement Support</span>
        </div>
      </div>
    </div>
  </section>
);

/* ================================ SECTION 2 ============================== */
const Welcome = () => (
  <section className="bg-white py-16 md:py-20">
    <div className="container mx-auto max-w-7xl px-4 md:px-6 grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
      <div>
        <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Welcome to Noida</span>
        <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
          A New Kind of Campus — Built for the AI Era
        </h2>
        <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
          The Noida Experience Centre is not another classroom. It is a real-world innovation studio where students, professionals, and career-shifters walk in curious and walk out capable. Under one roof: live-training studios, hands-on labs, industry mentors, and a global-standard learning environment.
        </p>
        <ul className="mt-6 space-y-3 text-slate-700">
          {[
            'Global curriculum, tailored for Indian careers',
            'Learn from practitioners actively working with AI at scale',
            'Live projects with real brands, real deliverables',
            'Career support until you land — not just certification'
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="text-[15px]">{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative">
        <div className="absolute -inset-4 bg-gradient-to-br from-orange-200 to-amber-100 rounded-3xl blur-2xl opacity-70" />
        <img src={IMG.manPhone} alt="AI experience" className="relative rounded-3xl w-full h-auto shadow-2xl border border-slate-200" />
      </div>
    </div>
  </section>
);

/* ================================ SECTION 3 ============================== */
const WhyAI = () => {
  const stats = [
    { value: '97M', label: 'New AI jobs by 2030 (WEF)', icon: <TrendingUp className="w-5 h-5" /> },
    { value: '3×', label: 'Higher salary for AI-fluent talent', icon: <Award className="w-5 h-5" /> },
    { value: '87%', label: 'Companies now AI-first hiring', icon: <Briefcase className="w-5 h-5" /> },
    { value: 'Every', label: 'Industry is being rewritten by AI', icon: <Zap className="w-5 h-5" /> },
  ];
  return (
    <section className="bg-slate-50 py-16 md:py-20">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Why AI, Why Now</span>
          <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
            The world just quietly changed. Every job is now an AI job.
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg">
            Whether you're in marketing, design, engineering, HR, sales or finance — AI has already entered your workflow. The question is whether you're leading it or being left behind.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">{s.icon}</div>
              <div className="mt-4 text-3xl md:text-4xl font-black text-slate-900">{s.value}</div>
              <div className="mt-1 text-sm text-slate-600">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 4 ============================== */
const CoursesLive = () => {
  const courses = [
    {
      title: 'Performance Marketing – 100% Job Guarantee',
      duration: '4 Months · Hybrid',
      tag: 'LIVE NOW',
      desc: 'Master Meta Ads, Google Ads, Analytics & AI-powered growth. Job guarantee.',
      to: '/performance-marketing'
    },
    {
      title: 'Digital Growth & Performance Marketing (CSDGPM)',
      duration: '6 Months · Specialist',
      tag: 'LIVE NOW',
      desc: 'Exemplar Global accredited specialist certification for high-growth marketing careers.',
      to: '/csdgpm-offer'
    },
    {
      title: 'AI Bootcamp for School & College Students',
      duration: '8 Weeks · Immersive',
      tag: 'LIVE NOW',
      desc: 'Prompt engineering, no-code AI tools, and building AI products end-to-end.',
      to: '/ai-bootcamp'
    },
  ];
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Courses Live Now</span>
            <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">Learning in-progress. Enrolments open.</h2>
          </div>
          <Link to="/#courses" className="text-sm font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
            View All Programs <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {courses.map((c, i) => (
            <div key={i} className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-orange-400 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[10px] font-black bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1 uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {c.tag}
                </span>
                <span className="text-xs text-slate-500">{c.duration}</span>
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900 leading-tight">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{c.desc}</p>
              <Link to={c.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 hover:text-orange-700">
                Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 5 ============================== */
const IndustryRoles = () => {
  const roles = [
    { icon: <Target className="w-6 h-6" />, title: 'AI Marketing Strategist', salary: '₹8-25 LPA' },
    { icon: <Bot className="w-6 h-6" />, title: 'Prompt Engineer', salary: '₹12-40 LPA' },
    { icon: <Layers className="w-6 h-6" />, title: 'AI Product Manager', salary: '₹18-50 LPA' },
    { icon: <Monitor className="w-6 h-6" />, title: 'AI-Powered Designer', salary: '₹6-20 LPA' },
    { icon: <Cpu className="w-6 h-6" />, title: 'ML/AI Engineer', salary: '₹15-45 LPA' },
    { icon: <Briefcase className="w-6 h-6" />, title: 'AI Business Analyst', salary: '₹9-28 LPA' },
  ];
  return (
    <section className="bg-slate-950 text-white py-16 md:py-20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={IMG.robotOffice} alt="AI office" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-950" />
      </div>
      <div className="relative container mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-400">Industry Roles</span>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black leading-tight">The AI roles employers are actually hiring for</h2>
          <p className="mt-4 text-slate-300 text-base md:text-lg">Based on live hiring data across India's top companies and unicorns — Q1 2026.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-orange-400/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center">{r.icon}</div>
              <h3 className="mt-4 font-bold text-lg">{r.title}</h3>
              <div className="mt-1 text-sm text-emerald-400 font-semibold">{r.salary}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 6 ============================== */
const HowWeTrain = () => {
  const pillars = [
    { icon: <Brain className="w-6 h-6" />, title: 'Live Studios', desc: 'Real classrooms, real mentors — no pre-recorded fluff.' },
    { icon: <Rocket className="w-6 h-6" />, title: 'Project-First', desc: 'Every module ends with a live deliverable, not a quiz.' },
    { icon: <Users className="w-6 h-6" />, title: 'Peer Cohorts', desc: 'Learn alongside driven career-shifters and future founders.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Global Certification', desc: 'Exemplar Global accredited credentials employers respect.' },
  ];
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">How We Train</span>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">Not a course. A career transformation.</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {pillars.map((p, i) => (
            <div key={i} className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-xl bg-white text-orange-600 flex items-center justify-center shadow-sm">{p.icon}</div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 7 ============================== */
const ExperienceCentre = () => (
  <section className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-20">
    <div className="container mx-auto max-w-7xl px-4 md:px-6">
      <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="absolute -inset-4 bg-gradient-to-br from-emerald-200 to-teal-100 rounded-3xl blur-2xl opacity-60" />
          <img src={IMG.vrCubes} alt="Immersive AI Experience" className="relative rounded-3xl w-full shadow-2xl border border-slate-200" />
        </div>
        <div className="order-1 lg:order-2">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Inside the Centre</span>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">A 6,000 sq ft playground for AI builders</h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg">Walk in, plug in, build. Our Noida centre is designed as a real-world AI innovation studio — so students, upskillers and founders can experience what "AI-first" actually feels like.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              'Immersive AI Labs',
              'Live Training Studios',
              'Prompt-Lab & Sandbox',
              'Career Mentor Pods',
              'Innovation Showcase Wall',
              'Founders\' Coworking Zone'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {item}
              </div>
            ))}
          </div>
          <div className="mt-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Noida Experience Centre Map"
                src={CENTRE.mapEmbed}
                width="100%"
                height="220"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-orange-500" /> {CENTRE.address}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-500" /> {CENTRE.hours}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ================================ SECTION 8 ============================== */
const StudentJourney = () => {
  const steps = [
    { n: '01', title: 'Discover', desc: 'Meet our counsellors, tour the centre, find your fit.' },
    { n: '02', title: 'Enrol', desc: 'Reserve your seat with a simple, transparent process.' },
    { n: '03', title: 'Train', desc: 'Live studios, projects, mentors — every single week.' },
    { n: '04', title: 'Build', desc: 'Ship real portfolio work with real brands.' },
    { n: '05', title: 'Get Hired', desc: 'Placement cell, referrals, mock interviews — until you land.' },
  ];
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Student Journey</span>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">From curious visitor to hired professional.</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="relative bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:shadow-md hover:border-orange-300 transition-all">
              <div className="text-4xl font-black text-orange-500/30">{s.n}</div>
              <h3 className="mt-2 text-lg font-bold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 9 ============================== */
const UpcomingSchools = () => {
  const cities = [
    { name: 'Bengaluru', status: 'Coming Q2 2026' },
    { name: 'Mumbai', status: 'Coming Q3 2026' },
    { name: 'Hyderabad', status: 'Coming Q3 2026' },
    { name: 'Delhi NCR', status: 'Now Open · Noida' },
    { name: 'Pune', status: 'Coming Q4 2026' },
    { name: 'Chennai', status: 'Coming Q4 2026' },
  ];
  return (
    <section className="bg-slate-50 py-16 md:py-20">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Upcoming AI Schools</span>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">AI School For All — nationwide rollout in 2026.</h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg">Noida is our flagship. Here's where we're headed next.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cities.map((c, i) => {
            const live = c.status.includes('Now Open');
            return (
              <div key={i} className={`rounded-2xl p-6 border transition-all ${live ? 'bg-emerald-600 text-white border-emerald-700 shadow-lg' : 'bg-white border-slate-200 hover:shadow-md'}`}>
                <div className="flex items-center gap-3">
                  <Building2 className={`w-6 h-6 ${live ? 'text-white' : 'text-orange-500'}`} />
                  <h3 className="text-lg font-bold">{c.name}</h3>
                </div>
                <div className={`mt-3 text-sm font-semibold ${live ? 'text-emerald-100' : 'text-slate-500'}`}>{c.status}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ================================ SECTION 10 ============================= */
const BookVisit = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: 'AI Experience Centre Visit', message: '' });
  const [status, setStatus] = useState({ loading: false, ok: null, err: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setStatus({ loading: false, ok: false, err: 'Please fill name, email and phone.' });
      return;
    }
    setStatus({ loading: true, ok: null, err: '' });
    try {
      await axios.post(`${BACKEND}/api/leads`, {
        ...form,
        source: 'noida_visit',
      });
      setStatus({ loading: false, ok: true, err: '' });
      setForm({ name: '', email: '', phone: '', course: 'AI Experience Centre Visit', message: '' });
    } catch (err) {
      setStatus({ loading: false, ok: false, err: 'Something went wrong. Please try again or call us directly.' });
    }
  };

  return (
    <section id="book-visit" className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white py-16 md:py-24">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Book Your Free Visit
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-black leading-tight">
            Come experience the future of learning — on us.
          </h2>
          <p className="mt-4 text-white/90 text-base md:text-lg">
            Free 45-minute walkthrough with our counsellors. Tour the labs, meet mentors, sit in on a live class. No obligation.
          </p>
          <div className="mt-8 space-y-3 text-white/95">
            <div className="flex items-start gap-3"><MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" /> <span>{CENTRE.address}</span></div>
            <div className="flex items-start gap-3"><Phone className="w-5 h-5 mt-0.5 flex-shrink-0" /> <a className="hover:underline" href={`tel:${CENTRE.phone}`}>{CENTRE.phone}</a></div>
            <div className="flex items-start gap-3"><Clock className="w-5 h-5 mt-0.5 flex-shrink-0" /> <span>{CENTRE.hours}</span></div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="noida-visit-form" className="bg-white text-slate-900 rounded-3xl p-6 md:p-8 shadow-2xl">
          <h3 className="text-2xl font-black">Reserve Your Slot</h3>
          <p className="text-sm text-slate-500 mt-1">Our team will confirm within a few hours.</p>
          <div className="mt-6 space-y-4">
            <input
              type="text" placeholder="Your Full Name" required
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="noida-form-name"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="email" placeholder="Email Address" required
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="noida-form-email"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="tel" placeholder="Phone (WhatsApp preferred)" required
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="noida-form-phone"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <select
              value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}
              data-testid="noida-form-course"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option>AI Experience Centre Visit</option>
              <option>Performance Marketing – Job Guarantee</option>
              <option>CSDGPM Specialist Program</option>
              <option>AI Bootcamp for Students</option>
              <option>Just exploring / General Enquiry</option>
            </select>
            <textarea
              placeholder="Anything specific you want to see? (optional)"
              rows="3"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="noida-form-message"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              type="submit"
              disabled={status.loading}
              data-testid="noida-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-70 text-white font-bold rounded-xl px-6 py-3.5 text-base shadow-lg transition-all"
            >
              {status.loading ? 'Submitting…' : <>Confirm My Visit <Send className="w-4 h-4" /></>}
            </button>
            {status.ok === true && (
              <div data-testid="noida-form-success" className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm">
                Thank you! We've received your request. Our team will call you shortly on {form.phone || 'your number'} to confirm.
              </div>
            )}
            {status.ok === false && (
              <div data-testid="noida-form-error" className="text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">{status.err}</div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

/* ================================== PAGE ================================= */
const NoidaExperiencePage = () => {
  return (
    <>
      <PageMeta
        title="AI Experience Centre — Sector 62, Noida | AI School For All"
        description="Step inside India's flagship AI Experience Centre in Noida. Live studios, immersive labs, industry mentors, career support. Book your free visit today."
        path="/noida"
      />
      <SiteHeader ctaLabel="Book Your Visit" ctaTo="/noida#book-visit" />
      <main data-testid="noida-experience-page">
        <Hero />
        <Welcome />
        <WhyAI />
        <CoursesLive />
        <IndustryRoles />
        <HowWeTrain />
        <ExperienceCentre />
        <StudentJourney />
        <UpcomingSchools />
        <BookVisit />
      </main>
      <SiteFooter />
    </>
  );
};

export default NoidaExperiencePage;
